import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Icon, IconButton, tokens } from '@hotjar/hotsauce';
import { useCopyToClipboard } from 'react-use';
import { useCurrentSiteId } from '@hotjar/site';
import { ShortLinkDropdownSkeletonLoader } from './ShortLinkDropdownSkeletonLoader';
import { useShortUrl } from './use-short-url';
import type { RequestState } from './use-short-url';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const TitleContainer = styled.div`
  ${tokens.text('subtitle1')};
  margin-bottom: ${tokens.spacer.x3};
`;

const LinkContainer = styled.div`
  display: flex;
`;

const LinkIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
`;

const OutputWrapper = styled.div`
  height: 40px;

  border-radius: 4px;
  border: 1px solid ${tokens.BRAND_COLORS.neutral_200};

  background-color: ${tokens.BRAND_COLORS.neutral_100};
  color: ${tokens.color.text_default};
  cursor: pointer;

  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
  padding-right: ${tokens.spacer.x0};
  margin-right: ${tokens.spacer.x1};

  width: 236px;

  overflow: hidden;
`;

const Output = styled.output`
  ${tokens.text('body2')};

  border-radius: 4px;

  background-color: transparent;
  color: ${tokens.color.text_default};
  cursor: pointer;

  box-sizing: border-box;
  display: block;
  flex-grow: 1;
  padding: 0px;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledIconButton = styled(IconButton)`
  > button {
    color: ${tokens.BRAND_COLORS.midnight_600};

    &:hover {
      color: ${tokens.BRAND_COLORS.midnight_600};
    }
  }
`;

export interface ShortLinkDropdownProps {
  title?: string;
  onCopy: () => void;
  shortUrl?: RequestState;
}

export const ShortLinkDropdown = forwardRef<HTMLOutputElement, ShortLinkDropdownProps>(
  ({ title, onCopy, shortUrl }, ref): JSX.Element | null => {
    const { t } = useTranslation();
    const [{ value: copiedValue }, copyToClipboard] = useCopyToClipboard();
    const currentSiteId = useCurrentSiteId();
    const defaultShortUrl = useShortUrl(currentSiteId || '', window.location.href);
    const actualShortUrl = shortUrl ?? defaultShortUrl;

    if (actualShortUrl.status !== 'success') {
      return <ShortLinkDropdownSkeletonLoader />;
    }

    const inCopiedState = copiedValue === actualShortUrl.data;
    const toolTipText = inCopiedState
      ? t('common:actions.copy-success')
      : t('common:actions.copy-label');

    return (
      <Wrapper>
        <TitleContainer>{title ?? t('common:actions.share')}</TitleContainer>
        <LinkContainer>
          <OutputWrapper>
            <LinkIcon icon="hyperlink" />
            <Output ref={ref}>{actualShortUrl.data}</Output>
          </OutputWrapper>

          <StyledIconButton
            onClick={() => {
              onCopy();
              copyToClipboard(actualShortUrl?.data as string);
            }}
            icon={inCopiedState ? 'check_alt' : 'copy'}
            description={toolTipText}
            tooltipText={toolTipText}
            data-testId="shortlink-copy-button"
          />
        </LinkContainer>
      </Wrapper>
    );
  },
);
